import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import { HandlePosition, RelationshipCardinality } from "skedyul-db/enums";

// Define input and output types from router
type RouterInput = inferRouterInputs<AppRouter>;
type RouterOutput = inferRouterOutputs<AppRouter>;

// Output types
export type GetModelOutput = RouterOutput["crm"]["getCRM"]["models"][number];
export type GetRelationshipOutput =
  RouterOutput["crm"]["getCRM"]["relationships"][number];
export type GetNormalizationOutput =
  RouterOutput["crm"]["getNormalizations"][number];

// Input types
export type CreateModelInput = RouterInput["crm"]["createModel"];
export type UpdateModelInput = RouterInput["crm"]["updateModel"];
export type CreateFieldInput = RouterInput["crm"]["createField"];
export type UpdateFieldInput = RouterInput["crm"]["updateField"];
export type CreateFieldDefinitionOptionInput =
  RouterInput["crm"]["addFieldDefinitionOption"];
export type RemoveFieldDefinitionOptionInput =
  RouterInput["crm"]["removeFieldDefinitionOption"];
export type UpdateFieldDefinitionOptionInput =
  RouterInput["crm"]["updateFieldDefinitionOption"];

export type CreateRelationshipInput = RouterInput["crm"]["createRelationship"];
export type DeleteRelationshipInput = { relationshipId: string };
export type UpdateCRMPositionsInput = RouterInput["crm"]["updateCRMPositions"];

export type UpdateRelationshipInput = {
  sourceModel?: GetModelOutput;
  targetModel?: GetModelOutput;
  cardinality?: RelationshipCardinality;
  handle?: string;
  reverseAssociationHandle?: string;
  label?: string;
  reverseAssociationLabel?: string;
  sourceHandlePosition?: HandlePosition;
  targetHandlePosition?: HandlePosition;
  onDeleteBehaviorForward?: "NONE" | "CASCADE" | "RESTRICT" | "SET_NULL";
  onDeleteBehaviorReverse?: "NONE" | "CASCADE" | "RESTRICT" | "SET_NULL";
};

export interface CRMField {
  id: string;
  label: string;
  relationship?: unknown;
  [key: string]: unknown; // Allow any other properties
}

// Custom field type with optional id for definition options
export type CRMFieldWithOptionalOptionId = Omit<
  GetModelOutput["fields"][number],
  "definition" | "section"
> & {
  definition?: {
    handle?: string;
    type?: string;
    id?: string;
    limitChoices?: number;
    regexPattern?: string | null;
    linkedFieldId?: string;
    isPrivate?: boolean;
    normalizations?: Array<{
      handle: string;
      sortOrder: number;
      arguments: unknown;
    }>;
    options?: Array<{
      id?: string;
      value: string;
      label: string;
      icon?: string | null;
      color?: string | null;
    }>;
    [key: string]: unknown;
  } | null;
  aiMeta?: {
    description?: string;
    mutability?: "always" | "restricted" | "create_only" | "immutable";
  } | null;
  /** Optional id while drafting a section before the field is saved. */
  section?: { id?: string; handle: string; name: string } | null;
};

// Define the state interface
export interface CRMState {
  relationships: {
    data: GetRelationshipOutput[];
    isLoading: boolean;
    error: string | null;
  };
  relationship: {
    data: GetRelationshipOutput | null;
    isLoading: boolean;
    error: string | null;
  };
  models: {
    data: GetModelOutput[];
    isLoading: boolean;
    error: string | null;
  };
  model: {
    data: GetModelOutput | null;
    options: {
      handleAutoGeneration: boolean;
    };
    isLoading: boolean;
    isValidating: boolean;
    error: string | null;
  };
  normalizations: {
    data: GetNormalizationOutput[];
    isLoading: boolean;
    error: string | null;
  };
  field: {
    data: CRMFieldWithOptionalOptionId | null;
    options: {
      regexEnabled: boolean;
      handleAutoGeneration: boolean;
      originalDefaultValue: {
        value: string[] | boolean[] | number[];
        optionIds: string[];
      };
    };
    isValidating: boolean;
    isLoading: boolean;
    error: Record<string, string> | null;
  };
  communicationChannels: {
    data: Array<{
      id: string;
      name: string;
      handle: string;
      icon: string;
      identifierValue: string;
      appInstallationId: string;
      appIconUrl?: string;
    }>;
    isLoading: boolean;
    error: string | null;
  };
  selectedModelId: string | null;
  isLoading: boolean;
  error: string | null;
}

// Type helper for selectors and thunks to avoid circular dependency with RootState
type StateWithCRM = { crm: CRMState };

// Initial state
const initialState: CRMState = {
  relationships: {
    data: [],
    isLoading: false,
    error: null,
  },
  relationship: {
    data: null,
    isLoading: false,
    error: null,
  },
  models: {
    data: [],
    isLoading: false,
    error: null,
  },
  normalizations: {
    data: [],
    isLoading: false,
    error: null,
  },
  field: {
    data: null,
    options: {
      regexEnabled: false,
      handleAutoGeneration: true,
      originalDefaultValue: {
        value: [],
        optionIds: [],
      },
    },
    isValidating: false,
    isLoading: false,
    error: null,
  },
  model: {
    data: {
      id: "",
      name: "",
      handle: "",
      labelTemplate: "",
      imageEnabled: false,
      imageSeedTemplate: "",
      positionX: 0,
      positionY: 0,
      fields: [],
      namePlural: "",
      handlePlural: "",
      description: "",
    },
    options: {
      handleAutoGeneration: true,
    },
    isValidating: false,
    isLoading: false,
    error: null,
  },
  communicationChannels: {
    data: [],
    isLoading: false,
    error: null,
  },
  selectedModelId: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchCRM = createAsyncThunk(
  "crm/fetchCRM",
  async (_, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.getCRM.query();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch CRM data",
      );
    }
  },
);

export const fetchNormalizations = createAsyncThunk(
  "crm/fetchNormalizations",
  async (_, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.getNormalizations.query();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch normalizations",
      );
    }
  },
);

export const fetchCommunicationChannels = createAsyncThunk(
  "crm/fetchCommunicationChannels",
  async (_, { rejectWithValue }) => {
    try {
      const result =
        await trpc.communicationChannels.getCommunicationChannels.query();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch communication channels",
      );
    }
  },
);

export const createModel = createAsyncThunk(
  "crm/createModel",
  async (input: CreateModelInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.createModel.mutate(input);

      // dispatch(updateWorkplace(result.navigationItem))

      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to create model",
      );
    }
  },
);

export const updateModel = createAsyncThunk(
  "crm/updateModel",
  async (input: UpdateModelInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.updateModel.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to update model",
      );
    }
  },
);

export const deleteModel = createAsyncThunk(
  "crm/deleteModel",
  async (input: { modelId: string }, { rejectWithValue }) => {
    try {
      // Fire-and-forget: enqueue background workflow; API returns { accepted: true }
      const result = await trpc.crm.deleteModel.mutate(input);
      // Return payload for optimistic update including navigation items to remove
      return {
        modelId: input.modelId,
        navigationItemsIds:
          (result.data as { navigationItemsIds?: string[] } | null)
            ?.navigationItemsIds ?? [],
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete model",
      );
    }
  },
);

export const deleteField = createAsyncThunk(
  "crm/deleteField",
  async (input: { fieldId: string }, { rejectWithValue }) => {
    try {
      // Use direct database deletion since there's no deleteField flow yet
      const result = await trpc.crm.deleteField.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete field",
      );
    }
  },
);

export const createField = createAsyncThunk(
  "crm/createField",
  async (input: CreateFieldInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.createField.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to create field",
      );
    }
  },
);

export const updateField = createAsyncThunk(
  "crm/updateField",
  async (input: UpdateFieldInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.updateField.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to update field",
      );
    }
  },
);

export const updateCRMPositions = createAsyncThunk(
  "crm/updateCRMPositions",
  async (input: UpdateCRMPositionsInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.updateCRMPositions.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to update CRM positions",
      );
    }
  },
);

export const createRelationship = createAsyncThunk(
  "crm/createRelationship",
  async (input: CreateRelationshipInput) => {
    return await trpc.crm.createRelationship.mutate(input);
  },
);

export const deleteRelationship = createAsyncThunk(
  "crm/deleteRelationship",
  async (input: DeleteRelationshipInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.deleteRelationship.mutate(input);
      // Handle migration approval case
      if ("requiresApproval" in result && result.requiresApproval) {
        return { requiresApproval: true, migrationId: result.migrationId };
      }
      return { relationshipId: input.relationshipId } as {
        relationshipId: string;
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to delete relationship",
      );
    }
  },
);

export const updateRelationshipLink = createAsyncThunk<
  GetRelationshipOutput,
  {
    relationshipId: string;
    linkId: string;
    onDeleteBehavior: "NONE" | "CASCADE" | "RESTRICT" | "SET_NULL";
    handlePosition?: "LEFT" | "RIGHT" | "TOP" | "BOTTOM";
  },
  { rejectValue: string }
>("crm/updateRelationshipLink", async (input, { rejectWithValue }) => {
  try {
    const result = await trpc.crm.updateRelationshipLink.mutate(input);
    if (!result.success || !result.data) {
      return rejectWithValue(
        result.errors[0]?.message ?? "Failed to update relationship link",
      );
    }
    return result.data as unknown as GetRelationshipOutput;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : "Failed to update relationship link",
    );
  }
});

export const updateFieldHandle = createAsyncThunk(
  "crm/updateFieldHandle",
  async (
    input: { handle: string; modelId: string; subdomain: string },
    { rejectWithValue },
  ) => {
    // Reject if handle is empty or not provided
    if (!input.handle?.trim()) {
      return rejectWithValue({
        code: "REQUIRED",
      });
    }

    // Validate handle format using regex
    const handleRegex = /^[a-zA-Z0-9_-]+$/;
    if (!handleRegex.test(input.handle)) {
      return rejectWithValue({
        code: "REGEX",
        message:
          "Handle can only contain letters, numbers, hyphens, and underscores",
      });
    }

    try {
      const result = await trpc.crm.validateFieldHandle.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue({
        code: "UNKNOWN",
      });
    }
  },
);

export const addFieldDefinitionOption = createAsyncThunk(
  "crm/addFieldDefinitionOption",
  async (
    input: CreateFieldDefinitionOptionInput,
    { rejectWithValue, getState },
  ) => {
    const state = getState() as StateWithCRM;
    const currentField = state.crm.field.data;

    // If it's a new field (no id), just return the option to add locally
    if (!currentField?.id || currentField.id === "new") {
      return { success: true, isLocal: true, option: input.option };
    }

    // If it's an existing field, make the server call
    try {
      const result = await trpc.crm.addFieldDefinitionOption.mutate(input);
      return { success: true, isLocal: false, result, option: input.option };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to add field definition option",
      );
    }
  },
);

export const removeFieldDefinitionOption = createAsyncThunk(
  "crm/removeFieldDefinitionOption",
  async (
    input: RemoveFieldDefinitionOptionInput,
    { rejectWithValue, getState },
  ) => {
    const state = getState() as StateWithCRM;
    const currentField = state.crm.field.data;

    // If it's a new field (no id), just return the option id to remove locally
    if (!currentField?.id || currentField.id === "new") {
      return { success: true, isLocal: true, optionId: input.optionId };
    }

    // If it's an existing field, make the server call
    try {
      const result = await trpc.crm.removeFieldDefinitionOption.mutate(input);
      return {
        success: true,
        isLocal: false,
        result,
        optionId: input.optionId,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to remove field definition option",
      );
    }
  },
);

export const updateFieldDefinitionOption = createAsyncThunk(
  "crm/updateFieldDefinitionOption",
  async (
    input: UpdateFieldDefinitionOptionInput,
    { rejectWithValue, getState },
  ) => {
    const state = getState() as StateWithCRM;
    const currentField = state.crm.field.data;

    // If it's a new field (no id), just return the updated option data locally
    if (!currentField?.id || currentField.id === "new") {
      return {
        success: true,
        isLocal: true,
        optionId: input.optionId,
        option: input.option,
      };
    }

    // If it's an existing field, make the server call
    try {
      const result = await trpc.crm.updateFieldDefinitionOption.mutate(input);
      return {
        success: true,
        isLocal: false,
        result,
        optionId: input.optionId,
        option: input.option,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to update field definition option",
      );
    }
  },
);

export const saveFieldDefinitionOption = createAsyncThunk(
  "crm/saveFieldDefinitionOption",
  async (
    input: {
      isEditing: boolean;
      optionId?: string;
      definitionId?: string;
      option: {
        label: string;
        value: string;
        color?: string;
        icon?: string;
      };
    },
    { rejectWithValue, getState },
  ) => {
    const { isEditing, optionId, definitionId, option } = input;
    const state = getState() as StateWithCRM;
    const currentField = state.crm.field.data;

    if (isEditing && optionId) {
      // Update existing option
      if (!currentField?.id || currentField.id === "new") {
        return { optionId };
      } else {
        try {
          const result = await trpc.crm.updateFieldDefinitionOption.mutate({
            optionId,
            option: {
              id: optionId,
              label: option.label,
              color: option.color,
              icon: option.icon,
            },
          });
          return { optionId, result };
        } catch (error) {
          return rejectWithValue(
            error instanceof Error
              ? error.message
              : "Failed to update field definition option",
          );
        }
      }
    } else if (definitionId) {
      // Add option to linked definition
      try {
        const result = await trpc.crm.addFieldDefinitionOption.mutate({
          fieldDefinitionId: definitionId,
          option,
        });
        return { result };
      } catch (error) {
        return rejectWithValue(
          error instanceof Error
            ? error.message
            : "Failed to add field definition option",
        );
      }
    } else {
      // Add option to local state only
      return {};
    }
  },
);

export const updateDefaultValueWithValidation = createAsyncThunk(
  "crm/updateDefaultValueWithValidation",
  async (
    input: {
      fieldId: string;
      defaultValue: { value: unknown[]; optionIds: string[] };
    },
    { rejectWithValue },
  ) => {
    const isEmpty = !input.defaultValue.value[0];

    if (isEmpty) {
      // If the value is empty, skip validation and just update the state
      return input.defaultValue;
    }
    try {
      // Validate the default value using the backend
      const validationResult = await trpc.crm.validateRecord.mutate({
        fieldId: input.fieldId,
        data: input.defaultValue.value,
      });
      if (!validationResult.result) {
        return rejectWithValue({
          error: validationResult.error,
        });
      }
      // If valid, return the new default value
      return input.defaultValue;
    } catch (error: unknown) {
      return rejectWithValue({
        error:
          (error && typeof error === "object" && "code" in error
            ? (error as { code: string }).code
            : undefined) ||
          (error instanceof Error
            ? error.message
            : "Failed to validate default value"),
      });
    }
  },
);

export const updateFieldPropWithAutoHandleAndValidate = createAsyncThunk(
  "crm/updateFieldPropWithAutoHandleAndValidate",
  async (
    input: { key: "label"; value: string; subdomain: string },
    { dispatch, getState },
  ) => {
    // First dispatch the regular update
    dispatch(updateFieldPropWithAutoHandle(input));

    // Get the current state after the update
    const state = getState() as StateWithCRM;
    const currentField = state.crm.field.data;
    const modelId = state.crm.model.data?.id;

    // If handle was auto-generated, validate it
    if (
      input.key === "label" &&
      currentField?.handle &&
      state.crm.field.options.handleAutoGeneration
    ) {
      await dispatch(
        updateFieldHandle({
          handle: currentField.handle,
          modelId: modelId || "",
          subdomain: input.subdomain,
        }),
      );
    }

    return input;
  },
);

export const validateModelHandle = createAsyncThunk(
  "crm/validateModelHandle",
  async (input: { handle: string; subdomain: string }, { rejectWithValue }) => {
    // Reject if handle is empty or not provided
    if (!input.handle?.trim()) {
      return rejectWithValue({
        code: "REQUIRED",
      });
    }

    // Validate handle format using regex
    const handleRegex = /^[a-zA-Z0-9_-]+$/;
    if (!handleRegex.test(input.handle)) {
      return rejectWithValue({
        code: "REGEX",
        message:
          "Handle can only contain letters, numbers, hyphens, and underscores",
      });
    }

    try {
      const result = await trpc.crm.validateModelHandle.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue({
        code: "UNKNOWN",
      });
    }
  },
);

export const updateModelPropWithAutoHandleAndValidate = createAsyncThunk(
  "crm/updateModelPropWithAutoHandleAndValidate",
  async (
    input: { key: "name"; value: string; subdomain: string },
    { dispatch, getState },
  ) => {
    // First dispatch the regular update with auto handle
    dispatch(updateModelPropWithAutoHandle(input));

    // Get the current state after the update
    const state = getState() as StateWithCRM;
    const currentModel = state.crm.model.data;

    // If handle was auto-generated, validate it
    if (
      input.key === "name" &&
      currentModel?.handle &&
      state.crm.model.options.handleAutoGeneration
    ) {
      await dispatch(
        validateModelHandle({
          handle: currentModel.handle,
          subdomain: input.subdomain,
        }),
      );
    }

    return input;
  },
);

// Create the slice
export const crmSlice = createSlice({
  name: "crm",
  initialState,
  reducers: {
    setSelectedModel: (state, action: PayloadAction<string | null>) => {
      state.selectedModelId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    scopeModel: (state, action: PayloadAction<string>) => {
      if (action.payload == "new") {
        state.model.data = initialState.model.data;
        // Enable handle auto-generation for new models
        state.model.options.handleAutoGeneration = true;
      } else {
        state.model.data =
          state.models.data.find((model) => model.id === action.payload) ||
          null;
        // Disable handle auto-generation for existing models
        state.model.options.handleAutoGeneration = false;
      }
    },
    scopeRelationship: (
      state,
      action: PayloadAction<{
        id: string;
        sourceModel?: GetModelOutput;
        targetModel?: GetModelOutput;
        cardinality?: RelationshipCardinality;
        handle?: string;
        reverseAssociationHandle?: string;
        label?: string;
        reverseAssociationLabel?: string;
        sourceHandlePosition?: HandlePosition;
        targetHandlePosition?: HandlePosition;
      }>,
    ) => {
      if (action.payload.id === "new") {
        // For new relationships, create a temporary relationship object
        state.relationship.data = {
          ...action.payload,
          workplaceId: "",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          links: [],
        } as unknown as GetRelationshipOutput;
      } else {
        // For existing relationships, find them in the data
        state.relationship.data =
          state.relationships.data.find(
            (relationship) => relationship.id === action.payload.id,
          ) || null;
      }
    },
    scopeField: (state, action: PayloadAction<string>) => {
      if (action.payload === "new") {
        // Create a new field template for the current model
        state.field.data = {
          id: "",
          label: "",
          handle: "",
          type: "STRING",
          description: null,
          appearance: null,
          requirement: "OPTIONAL",
          isUnique: false,
          system: false,
          defaultValue: {
            value: [],
            optionIds: [],
          },
          definition: {
            options: [],
            lazyLoadingEnabled: false,
            limitChoices: 1,
            isPrivate: true,
          },
          modelId: state.model.data?.id || "",
          relationshipLinkId: null,
          section: null,
        };
        // Enable handle auto-generation for new fields
        state.field.options.handleAutoGeneration = true;
        state.field.options.originalDefaultValue = {
          value: [],
          optionIds: [],
        };
      } else {
        /**
         * check if the definition is built-in
         */

        const existingField =
          state.model.data?.fields.find(
            (field) => field.id === action.payload,
          ) || null;
        state.field.data = existingField as CRMFieldWithOptionalOptionId;
        // Disable handle auto-generation for existing fields
        state.field.options.handleAutoGeneration = false;
        // Store the original default value with explicit empty array for value if not present
        state.field.options.originalDefaultValue = {
          value:
            (existingField?.defaultValue?.value as
              | string[]
              | boolean[]
              | number[]) ?? [],
          optionIds: existingField?.defaultValue?.optionIds || [],
        };
      }

      /**
       * show check
       */
      if (state.field.data?.definition?.regexPattern) {
        state.field.options.regexEnabled = true;
      } else {
        state.field.options.regexEnabled = false;
      }
    },
    updateModelProp: (
      state,
      action: PayloadAction<{
        key:
          | "handle"
          | "name"
          | "labelTemplate"
          | "description"
          | "imageSeedTemplate"
          | "imageEnabled";
        value: string | boolean;
      }>,
    ) => {
      const { key, value } = action.payload;
      if (state.model.data) {
        (state.model.data as Record<string, unknown>)[key] = value;
      }
    },
    updateModelContactLinks: (
      state,
      action: PayloadAction<
        Array<{
          id?: string;
          communicationChannelId: string;
          identifierFieldId: string;
          fieldMappings?: Record<string, string>;
          communicationChannel?: {
            id: string;
            name: string;
            handle: string;
            icon: string;
            identifierValue: string;
          };
          identifierField?: {
            id: string;
            label: string;
            handle: string;
          };
        }>
      >,
    ) => {
      if (state.model.data) {
        (state.model.data as Record<string, unknown>).contactAssociationLinks =
          action.payload;
      }
    },
    addModelContactLink: (
      state,
      action: PayloadAction<{
        id?: string;
        communicationChannelId: string;
        identifierFieldId: string;
        fieldMappings?: Record<string, string>;
        communicationChannel: {
          id: string;
          name: string;
          handle: string;
          icon: string;
          identifierValue: string;
        };
        identifierField: {
          id: string;
          label: string;
          handle: string;
        };
      }>,
    ) => {
      if (state.model.data) {
        const links =
          ((state.model.data as Record<string, unknown>)
            .contactAssociationLinks as Array<{
            communicationChannelId: string;
          }>) || [];
        const existingIndex = links.findIndex(
          (link) =>
            link.communicationChannelId ===
            action.payload.communicationChannelId,
        );
        if (existingIndex >= 0) {
          links[existingIndex] = {
            ...links[existingIndex],
            ...action.payload,
          };
        } else {
          links.push(action.payload);
        }
        (state.model.data as Record<string, unknown>).contactAssociationLinks =
          links;
      }
    },
    removeModelContactLink: (
      state,
      action: PayloadAction<{ communicationChannelId: string }>,
    ) => {
      if (state.model.data) {
        const links =
          ((state.model.data as Record<string, unknown>)
            .contactAssociationLinks as Array<{
            communicationChannelId: string;
          }>) || [];
        (state.model.data as Record<string, unknown>).contactAssociationLinks =
          links.filter(
            (l) =>
              l.communicationChannelId !==
              action.payload.communicationChannelId,
          );
      }
    },
    updateFieldProp: (
      state,
      action: PayloadAction<{
        key:
          | "label"
          | "description"
          | "icon"
          | "defaultValue"
          | "type"
          | "regexPattern"
          | "validationMessages"
          | "requirement"
          | "isUnique";
        value:
          | string
          | boolean
          | number
          | null
          | { value: unknown[]; optionIds: string[] }
          | Record<string, string>;
      }>,
    ) => {
      const { key, value } = action.payload;
      if (state.field.data) {
        (state.field.data as Record<string, unknown>)[key] = value;
      }
    },
    updateFieldAppearance: (
      state,
      action: PayloadAction<{
        key: "leftIcon" | "rightIcon" | "placeholder" | "helpText";
        value: string | null;
      }>,
    ) => {
      const { key, value } = action.payload;
      if (state.field.data) {
        const appearance = (state.field.data.appearance || {}) as Record<
          string,
          unknown
        >;
        appearance[key] = value;
        state.field.data.appearance = appearance;
      }
    },
    updateFieldAiMeta: (
      state,
      action: PayloadAction<{
        key: "description" | "mutability";
        value: string | undefined;
      }>,
    ) => {
      const { key, value } = action.payload;
      if (state.field.data) {
        if (!state.field.data.aiMeta) {
          state.field.data.aiMeta = {};
        }
        (state.field.data.aiMeta as Record<string, unknown>)[key] = value;
      }
    },
    updateFieldSectionLocal: (
      state,
      action: PayloadAction<{
        section: { id?: string; handle: string; name: string } | null;
      }>,
    ) => {
      if (state.field.data) {
        (state.field.data as CRMFieldWithOptionalOptionId).section =
          action.payload.section;
      }
    },
    renameFieldSectionInStore: (
      state,
      action: PayloadAction<{ handle: string; name: string; id?: string }>,
    ) => {
      const { handle, name, id } = action.payload;
      const apply = (
        section: { id?: string; handle: string; name: string } | null | undefined,
      ) => {
        if (!section) return section;
        if (section.handle !== handle && (!id || section.id !== id)) {
          return section;
        }
        return { ...section, name, ...(id ? { id } : {}) };
      };

      if (state.field.data) {
        state.field.data.section = apply(
          state.field.data.section as
            | { id?: string; handle: string; name: string }
            | null
            | undefined,
        ) as CRMFieldWithOptionalOptionId["section"];
      }

      for (const model of state.models.data) {
        for (const field of model.fields || []) {
          const current = (
            field as { section?: { id?: string; handle: string; name: string } }
          ).section;
          const next = apply(current);
          if (next !== current) {
            (field as { section?: typeof next }).section = next ?? undefined;
          }
        }
      }

      if (state.model.data?.fields) {
        for (const field of state.model.data.fields) {
          const current = (
            field as { section?: { id?: string; handle: string; name: string } }
          ).section;
          const next = apply(current);
          if (next !== current) {
            (field as { section?: typeof next }).section = next ?? undefined;
          }
        }
      }
    },
    setFieldDefaultValue: (
      state,
      action: PayloadAction<{ value: unknown[]; optionIds: string[] }>,
    ) => {
      if (state.field.data) {
        state.field.data.defaultValue = {
          value: action.payload.value,
          optionIds: action.payload.optionIds,
        };
      }
    },
    resetFieldDefinition: (
      state,
      action: PayloadAction<{
        handle: string;
      }>,
    ) => {
      if (state.field.data) {
        state.field.data.definition = {
          handle: action.payload.handle,
          isPrivate: true,
          options: [],
          limitChoices: 1,
          lazyLoadingEnabled: false,
          regexPattern: null,
        };
      }
    },
    addBuiltInDefinition: (
      state,
      action: PayloadAction<{
        id: string;
        handle: string;
        description: string;
      }>,
    ) => {
      if (state.field.data) {
        state.field.data.definition = {
          id: action.payload.id,
          isPrivate: false,
          handle: action.payload.handle,
          description: action.payload.description,
        };
      }
    },
    enableRegex: (state) => {
      state.field.options.regexEnabled = true;
    },
    disableRegex: (state) => {
      state.field.options.regexEnabled = false;
      // Clear the regexPattern value when regex is disabled
      if (state.field.data?.definition) {
        state.field.data.definition.regexPattern = null;
      }
    },
    enableHandleAutoGeneration: (state) => {
      state.field.options.handleAutoGeneration = true;
    },
    disableHandleAutoGeneration: (state) => {
      state.field.options.handleAutoGeneration = false;
    },
    disableModelHandleAutoGeneration: (state) => {
      state.model.options.handleAutoGeneration = false;
    },
    updateFieldPropWithAutoHandle: (
      state,
      action: PayloadAction<{
        key: "label";
        value: string;
        subdomain: string;
      }>,
    ) => {
      const { key, value, subdomain } = action.payload;
      if (state.field.data) {
        (state.field.data as Record<string, unknown>)[key] = value;

        // Set error if label is empty
        if (key === "label") {
          if (!value.trim()) {
            state.field.error = {
              ...state.field.error,
              label: "REQUIRED",
            };
          } else {
            // Clear only the label error if it exists
            if (state.field.error?.label) {
              const { label: _, ...remainingErrors } = state.field.error;
              state.field.error =
                Object.keys(remainingErrors).length > 0
                  ? remainingErrors
                  : null;
            }
          }
        }

        // Auto-generate handle if auto-generation is enabled
        if (key === "label" && state.field.options.handleAutoGeneration) {
          const generatedHandle = value
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

          // Compose the full handle: subdomain/modelHandle/fieldHandle
          const fullHandle =
            `${subdomain}/${state.model.data?.handle || ""}/${generatedHandle}`.replace(
              /\/+$/,
              "",
            );

          // Set the field handle
          state.field.data.handle = generatedHandle;

          // If the field has a definition that is not built-in, also auto-generate its handle
          if (
            state.field.data.definition &&
            state.field.data.definition.isPrivate
          ) {
            state.field.data.definition.handle = fullHandle;
          }
        }
      }
    },
    updateModelPropWithAutoHandle: (
      state,
      action: PayloadAction<{
        key: "name";
        value: string;
      }>,
    ) => {
      const { key, value } = action.payload;
      if (state.model.data) {
        (state.model.data as Record<string, unknown>)[key] = value;

        // Auto-generate handle if auto-generation is enabled
        if (key === "name" && state.model.options.handleAutoGeneration) {
          const generatedHandle = value
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");
          state.model.data.handle = generatedHandle;
        }
      }
    },
    updateFieldDefinitionProp: (
      state,
      action: PayloadAction<{
        key:
          | "limitChoices"
          | "regexPattern"
          | "options"
          | "normalizations"
          | "isPrivate";
        value:
          | number
          | string
          | null
          | Array<{
              id?: string;
              value: string;
              label: string;
              icon?: string | null;
              color?: string | null;
            }>
          | Array<{ handle: string; sortOrder: number; arguments: unknown }>
          | boolean;
      }>,
    ) => {
      const { key, value } = action.payload;
      if (state.field.data?.definition) {
        (state.field.data.definition as Record<string, unknown>)[key] = value;

        // Handle limitChoices conflicts with current default selections
        if (key === "limitChoices" && typeof value === "number" && value > 0) {
          if (state.field.data.defaultValue) {
            // Ensure arrays exist
            if (!Array.isArray(state.field.data.defaultValue.optionIds)) {
              state.field.data.defaultValue.optionIds = [];
            }
            if (!Array.isArray(state.field.data.defaultValue.value)) {
              state.field.data.defaultValue.value = [];
            }

            const currentOptionIds = state.field.data.defaultValue.optionIds;
            const currentValues = state.field.data.defaultValue.value;

            // If we have more defaults than the new limit, slice to fit
            if (currentOptionIds.length > value) {
              state.field.data.defaultValue.optionIds = currentOptionIds.slice(
                0,
                value,
              );
            }
            if (currentValues.length > value) {
              state.field.data.defaultValue.value = currentValues.slice(
                0,
                value,
              );
            }
          }
        }
      }
    },
    updateFieldDefinitionOptionDefault: (
      state,
      action: PayloadAction<{
        index: number;
        isDefault: boolean;
      }>,
    ) => {
      const { index, isDefault } = action.payload;
      if (!state.field.data?.definition?.options) return;

      const option = state.field.data.definition.options[index];
      if (!option) return;

      // Initialize defaultValue if it doesn't exist
      if (!state.field.data.defaultValue) {
        state.field.data.defaultValue = {
          value: [],
          optionIds: [],
        };
      }

      // Ensure optionIds array exists and is an array
      if (!Array.isArray(state.field.data.defaultValue.optionIds)) {
        state.field.data.defaultValue.optionIds = [];
      }

      // Ensure value array exists and is an array
      if (!Array.isArray(state.field.data.defaultValue.value)) {
        state.field.data.defaultValue.value = [];
      }

      const currentOptionIds = state.field.data.defaultValue.optionIds;
      const currentValues = state.field.data.defaultValue.value as (
        | string
        | boolean
        | number
      )[];
      const targetOptionId = option.id;
      const targetValue = option.value;

      if (isDefault) {
        // Add to defaults if not already present
        if (targetOptionId && !currentOptionIds.includes(targetOptionId)) {
          currentOptionIds.push(targetOptionId);
        }
        if (targetValue && !currentValues.includes(targetValue)) {
          currentValues.push(targetValue);
        }

        // For single-select fields, ensure only one option is default
        if (state.field.data.definition?.limitChoices === 1) {
          // Keep only the current option
          state.field.data.defaultValue.optionIds = targetOptionId
            ? [targetOptionId]
            : [];
          state.field.data.defaultValue.value = targetValue
            ? ([targetValue] as (string | number | boolean)[])
            : ([] as (string | number | boolean)[]);
        }
      } else {
        // Remove from defaults
        if (targetOptionId) {
          const idIndex = currentOptionIds.indexOf(targetOptionId);
          if (idIndex > -1) {
            currentOptionIds.splice(idIndex, 1);
          }
        }
        if (targetValue) {
          const valueIndex = currentValues.indexOf(
            targetValue as string | number | boolean,
          );
          if (valueIndex > -1) {
            currentValues.splice(valueIndex, 1);
          }
        }
      }
    },
    addFieldDefinitionOptionValue: (
      state,
      action: PayloadAction<{
        value: string;
        label: string;
        color?: string;
        icon?: string;
      }>,
    ) => {
      const { value, label, color, icon } = action.payload;

      // Add the option to the local field state
      if (state.field.data?.definition) {
        // Initialize options array if it doesn't exist
        if (!state.field.data.definition.options) {
          state.field.data.definition.options = [];
        }

        // Add the new option (without an id since it's local only)
        state.field.data.definition.options.push({
          value,
          label,
          color: color || null,
          icon: icon || null,
        });
      }
    },
    removeFieldDefinitionOptionValue: (
      state,
      action: PayloadAction<{
        value: string;
      }>,
    ) => {
      const { value } = action.payload;

      // Remove the option from the local field state by value
      if (state.field.data?.definition?.options) {
        // First, find the option that matches this value to get its ID
        const optionToRemove = state.field.data.definition.options.find(
          (option) => option.value === value,
        );

        // Remove the option from the options array
        state.field.data.definition.options =
          state.field.data.definition.options.filter(
            (option) => option.value !== value,
          );

        // Also remove from default values if it was selected as default
        if (state.field.data.defaultValue) {
          if (Array.isArray(state.field.data.defaultValue.value)) {
            state.field.data.defaultValue.value = (
              state.field.data.defaultValue.value as (
                | string
                | boolean
                | number
              )[]
            ).filter(
              (defaultValue: string | boolean | number) =>
                defaultValue !== value,
            );
          }

          // Remove the corresponding option ID from defaultValue.optionIds
          if (
            Array.isArray(state.field.data.defaultValue.optionIds) &&
            optionToRemove?.id
          ) {
            state.field.data.defaultValue.optionIds =
              state.field.data.defaultValue.optionIds.filter(
                (optionId) => optionId !== optionToRemove.id,
              );
          }
        }

        // Also update originalDefaultValue to keep it in sync
        if (state.field.options.originalDefaultValue) {
          if (Array.isArray(state.field.options.originalDefaultValue.value)) {
            state.field.options.originalDefaultValue.value = (
              state.field.options.originalDefaultValue.value as (
                | string
                | boolean
                | number
              )[]
            ).filter(
              (defaultValue: string | boolean | number) =>
                defaultValue !== value,
            ) as string[] | boolean[] | number[];
          }

          if (
            Array.isArray(state.field.options.originalDefaultValue.optionIds) &&
            optionToRemove?.id
          ) {
            state.field.options.originalDefaultValue.optionIds =
              state.field.options.originalDefaultValue.optionIds.filter(
                (optionId) => optionId !== optionToRemove.id,
              );
          }
        }
      }
    },
    revalidateDefaultValues: (state) => {
      if (!state.field.data?.definition?.options) {
        return;
      }

      const validOptionValues = new Set(
        state.field.data.definition.options.map((opt) => opt.value),
      );
      const validOptionIds = new Set(
        state.field.data.definition.options
          .filter((opt) => opt.id)
          .map((opt) => opt.id),
      );

      // Filter out default values that no longer exist in options
      if (state.field.data.defaultValue) {
        if (Array.isArray(state.field.data.defaultValue.value)) {
          state.field.data.defaultValue.value = (
            state.field.data.defaultValue.value as (string | boolean | number)[]
          ).filter((val) => validOptionValues.has(String(val)));
        }

        // Filter out option IDs that no longer exist
        if (Array.isArray(state.field.data.defaultValue.optionIds)) {
          state.field.data.defaultValue.optionIds =
            state.field.data.defaultValue.optionIds.filter((id) =>
              validOptionIds.has(id),
            );
        }
      }

      // Also update originalDefaultValue to keep it in sync
      if (state.field.options.originalDefaultValue) {
        if (Array.isArray(state.field.options.originalDefaultValue.value)) {
          state.field.options.originalDefaultValue.value = (
            state.field.options.originalDefaultValue.value as (
              | string
              | boolean
              | number
            )[]
          ).filter((val) => validOptionValues.has(String(val))) as
            | string[]
            | boolean[]
            | number[];
        }

        if (Array.isArray(state.field.options.originalDefaultValue.optionIds)) {
          state.field.options.originalDefaultValue.optionIds =
            state.field.options.originalDefaultValue.optionIds.filter((id) =>
              validOptionIds.has(id),
            );
        }
      }
    },
    updateRelationship: (
      state,
      action: PayloadAction<UpdateRelationshipInput>,
    ) => {
      state.relationship.data = {
        ...state.relationship.data,
        ...action.payload,
      } as GetRelationshipOutput;
    },
  },
  extraReducers: (builder) => {
    // Fetch CRM
    builder
      .addCase(fetchCRM.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCRM.fulfilled, (state, action) => {
        state.isLoading = false;
        state.models.data = action.payload.models;
        state.relationships.data = action.payload.relationships;
      })
      .addCase(fetchCRM.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Communication Channels
    builder
      .addCase(fetchCommunicationChannels.pending, (state) => {
        state.communicationChannels.isLoading = true;
        state.communicationChannels.error = null;
      })
      .addCase(fetchCommunicationChannels.fulfilled, (state, action) => {
        state.communicationChannels.isLoading = false;
        state.communicationChannels.data = action.payload.map((c) => ({
          id: c.id,
          name: c.name,
          handle: c.handle,
          icon: c.icon,
          identifierValue: c.identifierValue,
          appInstallationId: c.appInstallationId,
          appIconUrl: c.appInstallation?.app?.iconUrl ?? undefined,
        }));
      })
      .addCase(fetchCommunicationChannels.rejected, (state, action) => {
        state.communicationChannels.isLoading = false;
        state.communicationChannels.error = action.payload as string;
      });

    // Create Model
    builder
      .addCase(createModel.pending, (state) => {
        state.model.isLoading = true;
        state.error = null;
      })
      .addCase(createModel.fulfilled, (state, action) => {
        state.model.isLoading = false;
        // Skip if this was a migration that requires approval
        if (
          "requiresApproval" in action.payload &&
          action.payload.requiresApproval
        ) {
          return;
        }
        const payload = action.payload as unknown as {
          model: (typeof state.models.data)[0];
        };
        if (payload.model) {
          state.models.data.push(payload.model);
        }
      })
      .addCase(createModel.rejected, (state, action) => {
        state.model.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Model
    builder
      .addCase(updateModel.pending, (state) => {
        state.model.isLoading = true;
        state.error = null;
      })
      .addCase(updateModel.fulfilled, (state, action) => {
        state.model.isLoading = false;
        // Skip if this was a migration that requires approval
        if (
          "requiresApproval" in action.payload &&
          action.payload.requiresApproval
        ) {
          return;
        }
        const modelData = action.payload.data;
        if (!modelData) return;
        const index = state.models.data.findIndex(
          (model) => model.id === modelData.id,
        );
        if (index !== -1) {
          // Preserve the fields array from the existing model and merge with new model data
          const existingFields = state.models.data[index]?.fields || [];
          const updatedModel = {
            ...state.models.data[index],
            ...modelData,
            fields: existingFields,
          };
          state.models.data[index] =
            updatedModel as (typeof state.models.data)[0];
        }
      })
      .addCase(updateModel.rejected, (state, action) => {
        state.model.isLoading = false;
        state.error = action.payload as string;
      });

    // Create Field
    builder
      .addCase(createField.pending, (state) => {
        state.field.isLoading = true;
        state.error = null;
      })
      .addCase(createField.fulfilled, (state, action) => {
        state.field.isLoading = false;
        // Skip if this was a migration that requires approval
        if (
          action.payload &&
          "requiresApproval" in action.payload &&
          action.payload.requiresApproval
        ) {
          return;
        }
        const fieldData = action.payload
          .data as CRMFieldWithOptionalOptionId | null;
        if (!fieldData) return;
        const modelIndex = state.models.data.findIndex(
          (model) => model.id === fieldData.modelId,
        );
        if (modelIndex !== -1 && state.models.data[modelIndex]?.fields) {
          const fieldWithUnused = {
            ...fieldData,
            unused: fieldData.unused ?? true,
          };
          state.models.data[modelIndex]?.fields?.push(
            fieldWithUnused as unknown as (typeof state.models.data)[0]["fields"][0],
          );
        }

        /** preserve the isBuiltIn value */
        state.field.data = {
          ...fieldData,
          unused: fieldData.unused ?? true,
          definition: {
            ...(fieldData.definition as CRMFieldWithOptionalOptionId["definition"]),
          },
        };

        // Disable handle auto-generation for existing fields
        state.field.options.handleAutoGeneration = false;
        // Store the original default value with explicit empty array for value if not present
        state.field.options.originalDefaultValue = {
          value:
            (fieldData?.defaultValue?.value as
              | string[]
              | boolean[]
              | number[]) ?? [],
          optionIds: fieldData?.defaultValue?.optionIds || [],
        };
      })
      .addCase(createField.rejected, (state, action) => {
        state.field.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Field
    builder
      .addCase(updateField.pending, (state) => {
        state.field.isLoading = true;
        state.error = null;
      })
      .addCase(updateField.fulfilled, (state, action) => {
        state.field.isLoading = false;
        // Skip if this was a migration that requires approval
        if (
          action.payload &&
          "requiresApproval" in action.payload &&
          action.payload.requiresApproval
        ) {
          return;
        }
        const fieldData = action.payload
          .data as CRMFieldWithOptionalOptionId | null;
        if (!fieldData) return;

        for (let i = 0; i < state.models.data.length; i++) {
          const fields = state.models.data[i]?.fields;
          if (!fields) continue;

          const fieldIndex = fields.findIndex(
            (field) => field.id === fieldData.id,
          );
          if (fieldIndex >= 0) {
            const previousUnused = fields[fieldIndex]?.unused;
            fields[fieldIndex] = {
              ...fieldData,
              unused: fieldData.unused ?? previousUnused,
            } as unknown as (typeof state.models.data)[0]["fields"][0];
            break;
          }
        }

        // Update the field.options.originalDefaultValue if this is the scoped field
        if (state.field.data?.id === fieldData.id) {
          /** preserve the isBuiltIn value */
          state.field.data = {
            ...fieldData,
            definition: {
              ...(fieldData.definition as CRMFieldWithOptionalOptionId["definition"]),
            },
          };

          state.field.options.originalDefaultValue = {
            value:
              (fieldData.defaultValue?.value as
                | string[]
                | boolean[]
                | number[]) || [],
            optionIds: fieldData.defaultValue?.optionIds || [],
          };
        }
      })
      .addCase(updateField.rejected, (state, action) => {
        state.field.isLoading = false;
        state.error = action.payload as string;
      });

    // Update CRM positions (models and relationship links)
    builder
      .addCase(updateCRMPositions.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;

        // Update model positions optimistically
        if (action.meta.arg.models) {
          for (const update of action.meta.arg.models) {
            const modelIndex = state.models.data.findIndex(
              (model) => model.id === update.modelId,
            );
            if (modelIndex !== -1) {
              state.models.data[modelIndex]!.positionX = update.positionX;
              state.models.data[modelIndex]!.positionY = update.positionY;
            }
          }
        }

        // Update relationship link handle positions optimistically
        if (action.meta.arg.relationshipLinks) {
          for (const update of action.meta.arg.relationshipLinks) {
            for (const relationship of state.relationships.data) {
              const linkIndex = relationship.links?.findIndex(
                (link) => link.id === update.linkId,
              );
              if (
                linkIndex !== undefined &&
                linkIndex !== -1 &&
                relationship.links
              ) {
                relationship.links[linkIndex]!.handlePosition =
                  update.handlePosition;
              }
            }
          }
        }
      })
      .addCase(updateCRMPositions.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateCRMPositions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Relationship
    builder
      .addCase(createRelationship.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRelationship.fulfilled, (state, action) => {
        state.isLoading = false;
        // Skip if this was a migration that requires approval
        if (
          action.payload &&
          "requiresApproval" in action.payload &&
          action.payload.requiresApproval
        ) {
          return;
        }
        const relationshipData = action.payload.data as
          | (typeof state.relationships.data)[0]
          | null;
        if (relationshipData) {
          // add the relationship to the relationships.data array
          state.relationships.data.push(relationshipData);
        }
      })
      .addCase(createRelationship.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Relationship
    builder
      .addCase(deleteRelationship.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRelationship.fulfilled, (state, action) => {
        state.isLoading = false;
        // Skip if this was a migration that requires approval
        if (
          "requiresApproval" in action.payload &&
          action.payload.requiresApproval
        ) {
          return;
        }
        const payload = action.payload as { relationshipId: string };

        /**
         * delete the fields that are linked to the relationship
         */
        state.models.data.forEach((model) => {
          model.fields = model.fields.filter(
            (field) =>
              field.relationshipLinkId !==
                state.relationship.data?.links[0]?.id &&
              field.relationshipLinkId !==
                state.relationship.data?.links[1]?.id,
          );
        });

        // Remove the relationship from the relationships array
        state.relationships.data = state.relationships.data.filter(
          (relationship) => relationship.id !== payload.relationshipId,
        );

        // If the deleted relationship was the scoped one, clear it
        if (state.relationship.data?.id === payload.relationshipId) {
          state.relationship.data = null;
        }
      })
      .addCase(deleteRelationship.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Relationship Link
    builder
      .addCase(updateRelationshipLink.pending, (state) => {
        state.isLoading = true;
        state.relationship.isLoading = true;
        state.error = null;
        state.relationship.error = null;
      })
      .addCase(updateRelationshipLink.fulfilled, (state, action) => {
        state.isLoading = false;
        state.relationship.isLoading = false;

        const updatedRelationship = action.payload;

        // Update the relationship in the relationships array
        const index = state.relationships.data.findIndex(
          (r) => r.id === updatedRelationship.id,
        );
        if (index !== -1) {
          state.relationships.data[index] = updatedRelationship;
        }

        // Update the scoped relationship if it's the same one
        if (state.relationship.data?.id === updatedRelationship.id) {
          state.relationship.data = updatedRelationship;
        }
      })
      .addCase(updateRelationshipLink.rejected, (state, action) => {
        state.isLoading = false;
        state.relationship.isLoading = false;
        state.error = action.payload as string;
        state.relationship.error = action.payload as string;
      });

    // Validate Field Handle
    builder
      .addCase(updateFieldHandle.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;

        if (state.field.data) {
          // Preserve the prefix structure even when handle is empty
          const handlePath = `${action.meta.arg.subdomain}/${state.model.data?.handle || ""}${action.meta.arg.handle ? `/${action.meta.arg.handle}` : ""}`;

          state.field.data = {
            ...state.field.data,
            handle: action.meta.arg.handle,
            definition: {
              ...state.field.data.definition,
              handle: handlePath,
            },
          };
        }
      })
      .addCase(updateFieldHandle.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.isValid) {
          state.field.error = {
            ...state.field.error,
            handle: "UNIQUE",
          };
        } else {
          delete state.field.error?.handle;
        }
      })
      .addCase(updateFieldHandle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.field.error = {
          ...state.field.error,
          handle: (action.payload as { code: string }).code,
        };
      });

    // Add Field Definition Option
    builder
      .addCase(addFieldDefinitionOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addFieldDefinitionOption.fulfilled, (state, action) => {
        state.isLoading = false;

        // Add the option to the local field state regardless of whether it's new or existing
        if (state.field.data?.definition && action.payload.option) {
          if (!state.field.data.definition.options) {
            state.field.data.definition.options = [];
          }
          state.field.data.definition.options.push(
            action.payload.option as {
              id?: string;
              value: string;
              label: string;
              icon?: string | null;
              color?: string | null;
            },
          );
        }
      })
      .addCase(addFieldDefinitionOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Remove Field Definition Option
    builder
      .addCase(removeFieldDefinitionOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeFieldDefinitionOption.fulfilled, (state, action) => {
        state.isLoading = false;

        // Remove the option from the local field state regardless of whether it's new or existing
        if (state.field.data?.definition && action.payload.optionId) {
          if (state.field.data.definition.options) {
            // Find the option that's being removed to get its value
            const optionToRemove = state.field.data.definition.options.find(
              (option) => option.id === action.payload.optionId,
            );

            // Remove the option from the options array
            state.field.data.definition.options =
              state.field.data.definition.options.filter(
                (option) => option.id !== action.payload.optionId,
              );

            // Also remove from default values if it was selected as default
            if (optionToRemove && state.field.data.defaultValue) {
              if (Array.isArray(state.field.data.defaultValue.value)) {
                state.field.data.defaultValue.value = (
                  state.field.data.defaultValue.value as (
                    | string
                    | boolean
                    | number
                  )[]
                ).filter(
                  (defaultValue: string | boolean | number) =>
                    defaultValue !== optionToRemove.value,
                );
              }

              // Remove the corresponding option ID from defaultValue.optionIds
              if (
                Array.isArray(state.field.data.defaultValue.optionIds) &&
                optionToRemove.id
              ) {
                state.field.data.defaultValue.optionIds =
                  state.field.data.defaultValue.optionIds.filter(
                    (optionId) => optionId !== optionToRemove.id,
                  );
              }
            }

            // Also update originalDefaultValue to keep it in sync
            if (optionToRemove && state.field.options.originalDefaultValue) {
              if (
                Array.isArray(state.field.options.originalDefaultValue.value)
              ) {
                state.field.options.originalDefaultValue.value = (
                  state.field.options.originalDefaultValue.value as (
                    | string
                    | boolean
                    | number
                  )[]
                ).filter(
                  (defaultValue: string | boolean | number) =>
                    defaultValue !== optionToRemove.value,
                ) as string[] | boolean[] | number[];
              }

              if (
                Array.isArray(
                  state.field.options.originalDefaultValue.optionIds,
                ) &&
                optionToRemove.id
              ) {
                state.field.options.originalDefaultValue.optionIds =
                  state.field.options.originalDefaultValue.optionIds.filter(
                    (optionId) => optionId !== optionToRemove.id,
                  );
              }
            }
          }
        }
      })
      .addCase(removeFieldDefinitionOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Field Definition Option
    builder
      .addCase(updateFieldDefinitionOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFieldDefinitionOption.fulfilled, (state, action) => {
        state.isLoading = false;

        // Update the existing option in the local field state
        if (state.field.data?.definition && action.payload.optionId) {
          if (state.field.data.definition.options) {
            const optionIndex = state.field.data.definition.options.findIndex(
              (option) => option.id === action.payload.optionId,
            );
            if (optionIndex !== -1) {
              // Update only the updatable properties (label, icon, color)
              const existingOption =
                state.field.data.definition.options[optionIndex];
              if (existingOption) {
                if (action.payload.option.label) {
                  existingOption.label = action.payload.option.label;
                }
                if (action.payload.option.icon !== undefined) {
                  existingOption.icon = action.payload.option.icon;
                }
                if (action.payload.option.color !== undefined) {
                  existingOption.color = action.payload.option.color;
                }
              }
            }
          }
        }
      })
      .addCase(updateFieldDefinitionOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Model
    builder
      .addCase(deleteModel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteModel.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optimistic: remove model locally; related cleanup happens server-side
        state.models.data = state.models.data.filter(
          (model) => model.id !== action.payload.modelId,
        );
      })
      .addCase(deleteModel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Field
    builder
      .addCase(deleteField.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteField.fulfilled, (state, action) => {
        state.isLoading = false;

        // Remove the field from its model (server returns {accepted:true}, rely on meta arg)
        const fieldId = action.meta.arg.fieldId;
        state.models.data.forEach((model) => {
          model.fields = model.fields.filter((field) => field.id !== fieldId);
        });

        // If the deleted field was the scoped one, clear it
        if (state.field.data?.id === fieldId) {
          state.field.data = null;
        }
      })
      .addCase(deleteField.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Normalizations
    builder
      .addCase(fetchNormalizations.pending, (state) => {
        state.normalizations.isLoading = true;
        state.normalizations.error = null;
      })
      .addCase(fetchNormalizations.fulfilled, (state, action) => {
        state.normalizations.isLoading = false;
        state.normalizations.data = action.payload;
      })
      .addCase(fetchNormalizations.rejected, (state, action) => {
        state.normalizations.isLoading = false;
        state.normalizations.error = action.payload as string;
      });

    // Save Field Definition Option
    builder
      .addCase(saveFieldDefinitionOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveFieldDefinitionOption.fulfilled, (state, action) => {
        state.isLoading = false;

        const { isEditing, definitionId, option } = action.meta.arg;

        if (isEditing && action.payload?.optionId) {
          // Update existing option locally
          if (state.field.data?.definition?.options) {
            const optionIndex = state.field.data.definition.options.findIndex(
              (opt) => opt.id === action.payload.optionId,
            );
            if (optionIndex !== -1) {
              const existingOption =
                state.field.data.definition.options[optionIndex];
              if (existingOption) {
                existingOption.label = option.label;
                existingOption.color = option.color || null;
                existingOption.icon = option.icon || null;
              }
            }
          }
        } else if (definitionId) {
          // Add option to linked definition (remote)
          if (state.field.data?.definition) {
            if (!state.field.data.definition.options) {
              state.field.data.definition.options = [];
            }

            // Check if this is the first option being added
            const isFirstOption =
              state.field.data.definition.options.length === 0;

            const newOption: NonNullable<
              NonNullable<CRMFieldWithOptionalOptionId["definition"]>["options"]
            >[number] = {
              ...option,
              color: option.color || null,
              icon: option.icon || null,
            };

            // Add ID from payload if available
            const payloadResult = action.payload?.result;
            if (
              payloadResult &&
              payloadResult.data &&
              "id" in payloadResult.data &&
              typeof payloadResult.data.id === "string"
            ) {
              newOption.id = payloadResult.data.id;
            }

            state.field.data.definition.options.push(newOption);

            // Reset default value if this is the first option
            if (isFirstOption && state.field.data.defaultValue) {
              state.field.data.defaultValue = {
                value: [],
                optionIds: [],
              };
              // Clear defaultValue error when first option is added
              if (state.field.error?.defaultValue) {
                const { defaultValue: _, ...remainingErrors } =
                  state.field.error;
                state.field.error =
                  Object.keys(remainingErrors).length > 0
                    ? remainingErrors
                    : null;
              }
            }

            // Clear normalizations when adding an option
            state.field.data.definition.normalizations = [];
          }
        } else {
          // Add option to local state only
          if (state.field.data?.definition) {
            if (!state.field.data.definition.options) {
              state.field.data.definition.options = [];
            }

            // Check if this is the first option being added
            const isFirstOption =
              state.field.data.definition.options.length === 0;

            state.field.data.definition.options.push({
              value: option.value,
              label: option.label,
              color: option.color || null,
              icon: option.icon || null,
            });

            // Reset default value if this is the first option
            if (isFirstOption && state.field.data.defaultValue) {
              state.field.data.defaultValue = {
                value: [],
                optionIds: [],
              };
              // Clear defaultValue error when first option is added
              if (state.field.error?.defaultValue) {
                const { defaultValue: _, ...remainingErrors } =
                  state.field.error;
                state.field.error =
                  Object.keys(remainingErrors).length > 0
                    ? remainingErrors
                    : null;
              }
            }

            // Clear normalizations when adding an option
            state.field.data.definition.normalizations = [];
          }
        }
      })
      .addCase(saveFieldDefinitionOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Default Value with Validation
    builder
      .addCase(updateDefaultValueWithValidation.pending, (state, action) => {
        const isEmpty = !action.meta.arg.defaultValue.value[0];
        state.field.error = null;
        if (state.field.data) {
          state.field.data.defaultValue = {
            value: action.meta.arg.defaultValue.value,
            optionIds: action.meta.arg.defaultValue.optionIds,
          };
        }
        state.field.isValidating = !isEmpty;
      })
      .addCase(updateDefaultValueWithValidation.fulfilled, (state, action) => {
        state.field.isValidating = false;
        if (state.field.data) {
          state.field.data.defaultValue = action.payload;
        }
      })
      .addCase(updateDefaultValueWithValidation.rejected, (state, action) => {
        state.field.isValidating = false;
        if (
          action.payload &&
          typeof action.payload === "object" &&
          "error" in action.payload
        ) {
          const payload = action.payload as {
            error?: { code?: string } | string;
          };
          state.field.error = {
            defaultValue:
              (typeof payload.error === "object" && payload.error?.code) ||
              (typeof payload.error === "string" ? payload.error : undefined) ||
              "Failed to update default value",
          };
        } else {
          state.field.error = {
            defaultValue:
              action.error.message || "Failed to update default value",
          };
        }
      });

    // Validate Model Handle
    builder
      .addCase(validateModelHandle.pending, (state) => {
        state.model.isValidating = true;
        state.model.error = null;
      })
      .addCase(validateModelHandle.fulfilled, (state, action) => {
        state.model.isValidating = false;
        if (!action.payload.isValid) {
          state.model.error = "UNIQUE";
        } else {
          state.model.error = null;
        }
      })
      .addCase(validateModelHandle.rejected, (state, action) => {
        state.model.isValidating = false;
        state.model.error =
          (action.payload as { code: string })?.code || "UNKNOWN";
      });

    // Update Model Prop With Auto Handle And Validate
    builder
      .addCase(updateModelPropWithAutoHandleAndValidate.pending, (state) => {
        state.model.isLoading = true;
        state.error = null;
      })
      .addCase(updateModelPropWithAutoHandleAndValidate.fulfilled, (state) => {
        state.model.isLoading = false;
      })
      .addCase(
        updateModelPropWithAutoHandleAndValidate.rejected,
        (state, action) => {
          state.model.isLoading = false;
          state.error = action.payload as string;
        },
      );
  },
});

// Export actions
export const {
  setSelectedModel,
  clearError,
  scopeModel,
  scopeRelationship,
  scopeField,
  updateModelProp,
  updateModelContactLinks,
  addModelContactLink,
  removeModelContactLink,
  updateFieldProp,
  updateFieldAppearance,
  updateFieldAiMeta,
  updateFieldSectionLocal,
  renameFieldSectionInStore,
  setFieldDefaultValue,
  enableRegex,
  disableRegex,
  enableHandleAutoGeneration,
  disableHandleAutoGeneration,
  disableModelHandleAutoGeneration,
  updateFieldPropWithAutoHandle,
  updateModelPropWithAutoHandle,
  updateFieldDefinitionProp,
  updateFieldDefinitionOptionDefault,
  addFieldDefinitionOptionValue,
  removeFieldDefinitionOptionValue,
  revalidateDefaultValues,
  addBuiltInDefinition,
  resetFieldDefinition,
  updateRelationship,
} = crmSlice.actions;

// Export selectors
export const selectCRMModels = (state: StateWithCRM) => state.crm.models;
export const selectSelectedModelId = (state: StateWithCRM) =>
  state.crm.selectedModelId;
export const selectSelectedModel = (state: StateWithCRM) => {
  const { models, selectedModelId } = state.crm;
  if (!selectedModelId) return null;
  return models.data.find((model) => model.id === selectedModelId) || null;
};
export const selectCRMIsLoading = (state: StateWithCRM) => state.crm.isLoading;
export const selectCRMError = (state: StateWithCRM) => state.crm.error;

// Export reducer
export default crmSlice.reducer;
